import { useState, useEffect, useMemo } from "react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useGetExtra } from "../api/extras/useGetExtra";
import { useUpdateExtra } from "../api/extras/useUpdateExtra";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useGetCountries } from "../api/countries/useGetCountries";
import { useTranslation } from "../i18n/TranslationContext";
import type { Gender } from "../types";
import { GERMAN_PUBLIC_INSURANCE_COMPANIES } from "../data/insuranceCompanies";
import { isValidIBAN, isValidBIC } from "ibantools";
import taxIdValidator from "german-tax-id-validator";
import { toLocalDateString } from "../utils";
import { useDataAccuracyConfirmation } from "../hooks/useDataAccuracyConfirmation";

export function PayrollData() {
	const { t } = useTranslation();
	const { navigateDirectly, navigationContext } = useApp();

	// Get current user
	const { user } = useCurrentUser();

	// Fetch extra data and countries
	const { extra, loading: loadingExtra } = useGetExtra(user?.extraId);
	const { countries, loading: loadingCountries } = useGetCountries();
	const { updateExtra, loading: isSubmitting } = useUpdateExtra();

	// Check if user is internal extra (shouldn't see this screen)
	const isInternalExtra = user?.internal_extra === true;

	// Form state
	const [formData, setFormData] = useState({
		iban: "",
		bic: "",
		different_recipient: "",
		idnr: "",
		sozialversicherung: "",
		city_of_birth: "",
		country_of_birth: undefined as number | undefined,
		date_of_birth: "",
		geburtsname: "",
		gender: "" as Gender | "",
		insurance_type: "",
		insurance_company: "",
		haupt_ag: false,
	});

	const [noSozialversicherung, setNoSozialversicherung] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	// Data accuracy acknowledgment with localStorage persistence
	const { acknowledgesDataAccuracy, setAcknowledgesDataAccuracy } =
		useDataAccuracyConfirmation("payroll", user?.extraId);

	// Pre-fill form with existing data
	useEffect(() => {
		if (extra) {
			// Convert ISO date string to YYYY-MM-DD format for input type="date"
			let formattedDate = "";
			if (extra.date_of_birth) {
				// Format as YYYY-MM-DD using the centralized function
				formattedDate = toLocalDateString(extra.date_of_birth);
			}

			setFormData({
				iban: extra.iban || "",
				bic: extra.bic || "",
				different_recipient: extra.different_recipient || "",
				idnr: extra.idnr || "",
				sozialversicherung: extra.sozialversicherung || "",
				city_of_birth: extra.city_of_birth || "",
				country_of_birth: extra.country_of_birth,
				date_of_birth: formattedDate,
				geburtsname: extra.geburtsname || "",
				gender: (extra.gender as Gender) || "",
				insurance_type: extra.insurance_type || "",
				insurance_company: extra.insurance_company || "",
				haupt_ag: extra.haupt_ag || false,
			});

			// Set checkbox state based on whether sozialversicherung exists
			if (
				!extra.sozialversicherung &&
				(extra.city_of_birth || extra.geburtsname)
			) {
				setNoSozialversicherung(true);
			}
		}
	}, [extra]);

	const handleInputChange = (
		field: string,
		value: string | number | boolean | undefined
	) => {
		// Special handling for sozialversicherung field
		if (field === "sozialversicherung" && typeof value === "string") {
			// Limit to 12 characters
			let formattedValue = value.slice(0, 12);

			// Convert 9th character (index 8) to uppercase if it's a letter
			if (formattedValue.length >= 9) {
				const char9 = formattedValue.charAt(8);
				if (/[a-zA-Z]/.test(char9)) {
					formattedValue =
						formattedValue.slice(0, 8) +
						char9.toUpperCase() +
						formattedValue.slice(9);
				}
			}

			value = formattedValue;
		}

		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const handleCheckboxChange = (checked: boolean) => {
		setNoSozialversicherung(checked);
		if (checked) {
			// Clear sozialversicherung field when checkbox is checked
			setFormData((prev) => ({ ...prev, sozialversicherung: "" }));
			setErrors((prev) => ({ ...prev, sozialversicherung: "" }));
		} else {
			// Clear only payroll-specific birth fields when checkbox is unchecked
			// Note: Keep date_of_birth as it's a profile field that may have been set earlier
			setFormData((prev) => ({
				...prev,
				city_of_birth: "",
				country_of_birth: undefined,
				geburtsname: "",
			}));
			setErrors((prev) => ({
				...prev,
				city_of_birth: "",
				country_of_birth: "",
				geburtsname: "",
			}));
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		// IBAN validation
		if (!formData.iban.trim()) {
			newErrors.iban = t("payrollData.validation.ibanRequired");
		} else {
			// Remove spaces and validate IBAN format
			const ibanCleaned = formData.iban.replace(/\s/g, "");
			if (!isValidIBAN(ibanCleaned)) {
				newErrors.iban = t("payrollData.validation.ibanInvalid");
			}
		}

		// BIC validation
		if (!formData.bic.trim()) {
			newErrors.bic = t("payrollData.validation.bicRequired");
		} else {
			// Remove spaces and validate BIC format
			const bicCleaned = formData.bic.replace(/\s/g, "");
			// BIC must be 8 or 11 characters
			if (bicCleaned.length < 8 || bicCleaned.length > 11) {
				newErrors.bic = "BIC muss zwischen 8 und 11 Zeichen lang sein";
			} else if (!isValidBIC(bicCleaned)) {
				newErrors.bic = t("payrollData.validation.bicInvalid");
			}
		}

		// Steuer-ID validation (required field)
		if (!formData.idnr.trim()) {
			newErrors.idnr = t("payrollData.validation.idnrRequired");
		} else {
			// Remove spaces and validate Steuer-ID format
			const idnrCleaned = formData.idnr.replace(/\s/g, "");
			// Steuer-ID must be exactly 11 characters
			if (idnrCleaned.length !== 11) {
				newErrors.idnr = "Steuer-ID muss genau 11 Zeichen lang sein";
			} else if (!taxIdValidator.validate(idnrCleaned)) {
				newErrors.idnr = t("payrollData.validation.idnrInvalid");
			}
		}

		if (!formData.gender) {
			newErrors.gender = t("payrollData.validation.genderRequired");
		}
		if (!formData.insurance_type) {
			newErrors.insurance_type = t(
				"payrollData.validation.insuranceTypeRequired"
			);
		}
		if (!formData.insurance_company.trim()) {
			newErrors.insurance_company = t(
				"payrollData.validation.insuranceCompanyRequired"
			);
		}

		// Sozialversicherung OR checkbox must be filled
		if (!formData.sozialversicherung.trim() && !noSozialversicherung) {
			newErrors.sozialversicherung = t(
				"payrollData.validation.sozialversicherungOrCheckbox"
			);
		}

		// Sozialversicherung format validation (when provided)
		if (formData.sozialversicherung.trim()) {
			const svnr = formData.sozialversicherung.trim();

			// Must be exactly 12 characters
			if (svnr.length !== 12) {
				newErrors.sozialversicherung = t(
					"payrollData.validation.sozialversicherungLength"
				);
			}
			// 9th character (index 8) must be a capital letter
			else if (!/^.{8}[A-Z]/.test(svnr)) {
				newErrors.sozialversicherung = t(
					"payrollData.validation.sozialversicherung9thChar"
				);
			}
		}

		// If checkbox is checked, birth fields are required
		if (noSozialversicherung) {
			if (!formData.city_of_birth.trim()) {
				newErrors.city_of_birth = t(
					"payrollData.validation.cityOfBirthRequired"
				);
			}
			if (!formData.country_of_birth) {
				newErrors.country_of_birth = t(
					"payrollData.validation.countryOfBirthRequired"
				);
			}
			if (!formData.date_of_birth.trim()) {
				newErrors.date_of_birth = t(
					"payrollData.validation.dateOfBirthRequired"
				);
			}
			if (!formData.geburtsname.trim()) {
				newErrors.geburtsname = t("payrollData.validation.geburtsnameRequired");
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm() || !user?.extraId) {
			return;
		}

		// Prepare data for submission - remove spaces from IBAN, BIC, and Steuer-ID
		const updateData: Record<string, any> = {
			iban: formData.iban.replace(/\s/g, ""),
			bic: formData.bic.replace(/\s/g, ""),
			idnr: formData.idnr.replace(/\s/g, ""),
			gender: formData.gender,
			insurance_type: formData.insurance_type,
			insurance_company: formData.insurance_company,
			haupt_ag: formData.haupt_ag,
		};

		// Add optional fields
		if (formData.different_recipient.trim()) {
			updateData.different_recipient = formData.different_recipient;
		}

		// Add sozialversicherung or birth fields based on checkbox
		if (noSozialversicherung) {
			updateData.sozialversicherung = null;
			updateData.city_of_birth = formData.city_of_birth;
			updateData.country_of_birth = formData.country_of_birth;
			updateData.date_of_birth = formData.date_of_birth;
			updateData.geburtsname = formData.geburtsname;
		} else {
			updateData.sozialversicherung = formData.sozialversicherung;
			// Clear only payroll-specific birth fields if sozialversicherung is provided
			// Note: date_of_birth is a profile field and should NOT be cleared here
			updateData.city_of_birth = null;
			updateData.country_of_birth = null;
			updateData.geburtsname = null;
		}

		const result = await updateExtra(user.extraId, updateData);

		if (result) {
			// Navigate back to source screen
			if (navigationContext?.sourceScreen) {
				navigateDirectly(navigationContext.sourceScreen);
			} else {
				navigateDirectly("app-profile-completion-overview");
			}
		}
	};

	// Redirect internal extras
	if (isInternalExtra) {
		navigateDirectly("dashboard");
		return null;
	}

	// Loading state
	if (loadingExtra || loadingCountries) {
		return (
			<MainLayout
				showBack={true}
				onBack={() => {
					if (navigationContext?.sourceScreen) {
						navigateDirectly(navigationContext.sourceScreen);
					} else {
						navigateDirectly("app-profile-completion-overview");
					}
				}}
			>
				<div className="flex items-center justify-center min-h-screen">
					<div className="text-center">
						<div className="w-12 h-12 border-4 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
						<p className="text-[var(--ucm-text-medium)]">
							{t("common.loading")}
						</p>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout
			showBack={true}
			onBack={() => {
				if (navigationContext?.sourceScreen) {
					navigateDirectly(navigationContext.sourceScreen);
				} else {
					navigateDirectly("app-profile-completion-overview");
				}
			}}
		>
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
					{t("payrollData.title")}
				</h1>
				<p className="text-[var(--ucm-text-medium)]">
					{t("payrollData.subtitle")}
				</p>
			</div>

			<form onSubmit={handleSubmit} className="space-y-6">
				{/* Banking Information */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
					<h2 className="font-semibold text-[var(--ucm-text-dark)] mb-4">
						{t("payrollData.bankingInformation")}
					</h2>

					<div className="space-y-4">
						{/* IBAN */}
						<div>
							<label
								htmlFor="iban"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								IBAN <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="iban"
								value={formData.iban}
								onChange={(e) => handleInputChange("iban", e.target.value)}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.iban
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
								placeholder={t("payrollData.fields.ibanPlaceholder")}
							/>
							{errors.iban && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.iban}
								</p>
							)}
						</div>

						{/* BIC */}
						<div>
							<label
								htmlFor="bic"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								BIC/SWIFT <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="bic"
								value={formData.bic}
								onChange={(e) => handleInputChange("bic", e.target.value)}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.bic
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
								placeholder={t("payrollData.fields.bicPlaceholder")}
							/>
							{errors.bic && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.bic}
								</p>
							)}
						</div>

						{/* Different Recipient (optional) */}
						<div>
							<label
								htmlFor="different_recipient"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								{t("payrollData.fields.differentRecipient")}
							</label>
							<input
								type="text"
								id="different_recipient"
								value={formData.different_recipient}
								onChange={(e) =>
									handleInputChange("different_recipient", e.target.value)
								}
								className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors"
								placeholder={t(
									"payrollData.fields.differentRecipientPlaceholder"
								)}
							/>
							<p className="mt-1 text-sm text-[var(--ucm-text-light)]">
								{t("payrollData.fields.differentRecipientInfo")}
							</p>
						</div>
					</div>
				</div>

				{/* Tax Information */}
				<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
					<h2 className="font-semibold text-[var(--ucm-text-dark)] mb-4">
						{t("payrollData.taxInformation")}
					</h2>

					<div className="space-y-4">
						{/* Main Employer Checkbox */}
						<div className="flex items-center space-x-3">
							<input
								type="checkbox"
								id="haupt_ag"
								checked={formData.haupt_ag}
								onChange={(e) =>
									handleInputChange("haupt_ag", e.target.checked)
								}
								className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)]"
							/>
							<label
								htmlFor="haupt_ag"
								className="text-base font-medium text-[var(--ucm-text-dark)]"
							>
								{t("payrollData.fields.hauptAg")}
							</label>
							<div className="relative group">
								<svg
									className="w-5 h-5 text-[var(--ucm-info-blue)] cursor-help"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
										clipRule="evenodd"
									/>
								</svg>
								<div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-[var(--ucm-text-dark)] text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
									{t("payrollData.tooltips.hauptAg")}
									<div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-[var(--ucm-text-dark)]"></div>
								</div>
							</div>
						</div>

						{/* IDNR (required) */}
						<div>
							<div className="flex items-center space-x-2 mb-2">
								<label
									htmlFor="idnr"
									className="text-base font-medium text-[var(--ucm-text-medium)]"
								>
									{t("payrollData.fields.idnr")}{" "}
									<span className="text-red-500">*</span>
								</label>
								<div className="relative group">
									<svg
										className="w-5 h-5 text-[var(--ucm-info-blue)] cursor-help"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
											clipRule="evenodd"
										/>
									</svg>
									<div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-[var(--ucm-text-dark)] text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
										{t("payrollData.tooltips.idnr")}
										<div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-[var(--ucm-text-dark)]"></div>
									</div>
								</div>
							</div>
							<input
								type="text"
								id="idnr"
								value={formData.idnr}
								onChange={(e) => handleInputChange("idnr", e.target.value)}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.idnr
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
								placeholder={t("payrollData.fields.idnrPlaceholder")}
							/>
							{errors.idnr && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.idnr}
								</p>
							)}
						</div>

						{/* Sozialversicherung */}
						<div>
							<label
								htmlFor="sozialversicherung"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								{t("payrollData.fields.sozialversicherung")}{" "}
								<span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="sozialversicherung"
								value={formData.sozialversicherung}
								onChange={(e) =>
									handleInputChange("sozialversicherung", e.target.value)
								}
								disabled={noSozialversicherung}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.sozialversicherung
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								} ${noSozialversicherung ? "bg-gray-100 cursor-not-allowed" : ""}`}
								placeholder={t(
									"payrollData.fields.sozialversicherungPlaceholder"
								)}
							/>
							{errors.sozialversicherung && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.sozialversicherung}
								</p>
							)}
						</div>

						{/* No Sozialversicherung Checkbox */}
						<div className="flex items-center space-x-3">
							<input
								type="checkbox"
								id="no_sozialversicherung"
								checked={noSozialversicherung}
								onChange={(e) => handleCheckboxChange(e.target.checked)}
								className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)]"
							/>
							<label
								htmlFor="no_sozialversicherung"
								className="text-base font-medium text-[var(--ucm-text-dark)]"
							>
								{t("payrollData.fields.noSozialversicherung")}
							</label>
						</div>
						{!formData.sozialversicherung && !noSozialversicherung && (
							<p className="text-sm text-[var(--ucm-info-blue)]">
								{t("payrollData.fields.noSozialversicherungInfo")}
							</p>
						)}

						{/* Conditional Birth Fields */}
						{noSozialversicherung && (
							<div className="space-y-4 pt-4 border-t border-[var(--ucm-border-light)]">
								{/* City of Birth */}
								<div>
									<label
										htmlFor="city_of_birth"
										className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										Geburtsort <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="city_of_birth"
										value={formData.city_of_birth}
										onChange={(e) =>
											handleInputChange("city_of_birth", e.target.value)
										}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
											errors.city_of_birth
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
										placeholder={t("payrollData.fields.cityOfBirthPlaceholder")}
									/>
									{errors.city_of_birth && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.city_of_birth}
										</p>
									)}
								</div>

								{/* Country of Birth */}
								<div>
									<label
										htmlFor="country_of_birth"
										className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										Geburtsland <span className="text-red-500">*</span>
									</label>
									<select
										id="country_of_birth"
										value={formData.country_of_birth || ""}
										onChange={(e) =>
											handleInputChange(
												"country_of_birth",
												e.target.value ? parseInt(e.target.value) : undefined
											)
										}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
											errors.country_of_birth
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
									>
										<option value="">
											{t("payrollData.fields.countryOfBirthPlaceholder")}
										</option>
										{countries.map((country) => (
											<option
												key={country.country_id}
												value={country.country_id}
											>
												{country.country_name}
											</option>
										))}
									</select>
									{errors.country_of_birth && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.country_of_birth}
										</p>
									)}
								</div>

								{/* Date of Birth */}
								<div>
									<label
										htmlFor="date_of_birth"
										className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										Geburtsdatum <span className="text-red-500">*</span>
									</label>
									<input
										type="date"
										id="date_of_birth"
										value={formData.date_of_birth}
										onChange={(e) =>
											handleInputChange("date_of_birth", e.target.value)
										}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
											errors.date_of_birth
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
									/>
									{errors.date_of_birth && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.date_of_birth}
										</p>
									)}
								</div>

								{/* Geburtsname */}
								<div>
									<label
										htmlFor="geburtsname"
										className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										Geburtsname <span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="geburtsname"
										value={formData.geburtsname}
										onChange={(e) =>
											handleInputChange("geburtsname", e.target.value)
										}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
											errors.geburtsname
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
										placeholder={t("payrollData.fields.geburtsnamePlaceholder")}
									/>
									{errors.geburtsname && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.geburtsname}
										</p>
									)}
								</div>
							</div>
						)}

						{/* Gender */}
						<div>
							<label
								htmlFor="gender"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								Geschlecht <span className="text-red-500">*</span>
							</label>
							<select
								id="gender"
								value={formData.gender}
								onChange={(e) => handleInputChange("gender", e.target.value)}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.gender
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							>
								<option value="">
									{t("payrollData.fields.genderPlaceholder")}
								</option>
								<option value="male">
									{t("payrollData.genderOptions.male")}
								</option>
								<option value="female">
									{t("payrollData.genderOptions.female")}
								</option>
								<option value="divers">
									{t("payrollData.genderOptions.divers")}
								</option>
								<option value="no specified">
									{t("payrollData.genderOptions.noSpecified")}
								</option>
							</select>
							{errors.gender && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.gender}
								</p>
							)}
						</div>

						{/* Insurance Type */}
						<div>
							<label
								htmlFor="insurance_type"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								Versicherungsart <span className="text-red-500">*</span>
							</label>
							<select
								id="insurance_type"
								value={formData.insurance_type}
								onChange={(e) =>
									handleInputChange("insurance_type", e.target.value)
								}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.insurance_type
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
							>
								<option value="">
									{t("payrollData.fields.insuranceTypePlaceholder")}
								</option>
								<option value="gesetzlich">
									{t("payrollData.insuranceTypes.gesetzlich")}
								</option>
								<option value="privat">
									{t("payrollData.insuranceTypes.privat")}
								</option>
							</select>
							{errors.insurance_type && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.insurance_type}
								</p>
							)}
						</div>

						{/* Insurance Company (with autocomplete) */}
						<div>
							<label
								htmlFor="insurance_company"
								className="block text-base font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								Krankenkasse <span className="text-red-500">*</span>
							</label>
							<input
								type="text"
								id="insurance_company"
								list="insurance-companies-list"
								value={formData.insurance_company}
								onChange={(e) =>
									handleInputChange("insurance_company", e.target.value)
								}
								className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors ${
									errors.insurance_company
										? "border-[var(--ucm-error-red)]"
										: "border-[var(--ucm-border-light)]"
								}`}
								placeholder={t(
									"payrollData.fields.insuranceCompanyPlaceholder"
								)}
							/>
							<datalist id="insurance-companies-list">
								{GERMAN_PUBLIC_INSURANCE_COMPANIES.map((company) => (
									<option key={company} value={company} />
								))}
							</datalist>
							{errors.insurance_company && (
								<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
									{errors.insurance_company}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Data Accuracy Acknowledgment - Only for external users */}
				{!isInternalExtra && (
					<div className="bg-[var(--ucm-info-light-blue)] rounded-xl pb-4">
						<label className="flex items-start cursor-pointer">
							<input
								type="checkbox"
								checked={acknowledgesDataAccuracy}
								onChange={(e) => setAcknowledgesDataAccuracy(e.target.checked)}
								className="mt-1 w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)]"
							/>
							<span className="ml-3 text-sm text-[var(--ucm-text-dark)]">
								Ich bestätige, dass die obigen Angaben nach bestem Wissen
								korrekt sind. Bei Änderungen werde ich diese unverzüglich
								aktualisieren.
							</span>
						</label>
					</div>
				)}

				{/* Button Section */}
				<div className="space-y-3">
					{/* Submit Button */}
					<button
						type="submit"
						disabled={
							isSubmitting || (!isInternalExtra && !acknowledgesDataAccuracy)
						}
						className={`w-full h-12 font-semibold rounded-full transition-colors duration-200 ${
							isSubmitting || (!isInternalExtra && !acknowledgesDataAccuracy)
								? "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed"
								: "bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]"
						}`}
					>
						{isSubmitting ? t("common.saving") : t("payrollData.save")}
					</button>

					{/* Back Button */}
					<button
						type="button"
						onClick={() => {
							navigateDirectly("app-profile-completion-overview");
						}}
						className="w-full h-12 font-semibold rounded-full border border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
					>
						Zurück
					</button>
				</div>
			</form>
		</MainLayout>
	);
}
